import { useEffect, useRef, useState } from 'react';
import { GoogleMap, Polyline, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBus, faTrain, faPersonWalking, faMapMarkedAlt, faClock, faMoneyBill, faRoute, faTimes } from '@fortawesome/free-solid-svg-icons';
import './EpicGoogleMap.css';

const containerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '12px',
};

const overlayStyle = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

// Move libraries array outside component
const libraries = ['geometry'];

const EpicGoogleMap = ({ from, to, fullRoutes }) => {
  const mapRef = useRef(null);
  const [combinedPath, setCombinedPath] = useState([]);
  const [defaultCenter, setDefaultCenter] = useState({ lat: 3.139, lng: 101.6869 });
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [transitStops, setTransitStops] = useState([]);
  const [selectedStop, setSelectedStop] = useState(null);
  const [walkingPaths, setWalkingPaths] = useState([]);
  const [routeSummary, setRouteSummary] = useState(null);

  const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(false);

  const handleMarkerClick = (stop) => {
    // Ensure we're using the correct stop data
    const selectedStopData = transitStops.find(s => 
      s.position.lat === stop.position.lat && 
      s.position.lng === stop.position.lng
    );
    
    if (selectedStopData) {
      setSelectedStop(selectedStopData);
      setIsInfoWindowOpen(true);
    }
  };

  const handleInfoWindowClose = () => {
    setSelectedStop(null);
    setIsInfoWindowOpen(false);
  };

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  useEffect(() => {
    if (!isLoaded || !fullRoutes || !window.google?.maps?.geometry?.encoding) return;

    try {
      let allPoints = [];
      let stops = [];
      let walks = [];
      let totalDuration = 0;
      let totalDistance = 0;
      let totalFare = 0;

      fullRoutes.forEach((route) => {
        // Calculate route summary
        if (route.duration) {
          const durationMatch = route.duration.match(/(\d+)h\s*(\d+)min/);
          if (durationMatch) {
            totalDuration += parseInt(durationMatch[1]) * 60 + parseInt(durationMatch[2]);
          }
        }
        if (route.distance) {
          const distanceMatch = route.distance.match(/(\d+\.?\d*)km/);
          if (distanceMatch) {
            totalDistance += parseFloat(distanceMatch[1]);
          }
        }
        if (route.fare) {
          const fareMatch = route.fare.match(/RM(\d+)-(\d+)/);
          if (fareMatch) {
            totalFare = Math.max(totalFare, parseInt(fareMatch[2]));
          }
        }

        if (route.polyline) {
          const decoded = window.google.maps.geometry.encoding.decodePath(route.polyline);
          decoded.forEach((latLng) => {
            allPoints.push({
              lat: latLng.lat(),
              lng: latLng.lng(),
            });
          });
        }

        if (route.routeDetails) {
          route.routeDetails.forEach((detail) => {
            if (detail.travelMode === 'TRANSIT') {
              stops.push({
                position: {
                  lat: detail.startLocation.latitude,
                  lng: detail.startLocation.longitude
                },
                name: detail.transitLine?.name || 'Transit Stop',
                line: detail.transitLine?.shortName || '',
                color: detail.transitLine?.color || '#000000',
                departureTime: detail.departureTime,
                arrivalTime: detail.arrivalTime,
                instruction: detail.instruction,
                vehicleType: detail.transitLine?.vehicle?.type,
                agency: detail.transitLine?.agencies?.name,
                phone: detail.transitLine?.agencies?.phoneNo
              });
            } else if (detail.travelMode === 'WALK') {
              walks.push({
                start: {
                  lat: detail.startLocation.latitude,
                  lng: detail.startLocation.longitude
                },
                end: {
                  lat: detail.endLocation.latitude,
                  lng: detail.endLocation.longitude
                },
                instruction: detail.instruction,
                duration: detail.duration,
                distance: detail.distance
              });
            }
          });
        }
      });

      setCombinedPath(allPoints);
      setTransitStops(stops);
      setWalkingPaths(walks);
      setRouteSummary({
        totalDuration: `${Math.floor(totalDuration / 60)}h ${totalDuration % 60}min`,
        totalDistance: `${totalDistance.toFixed(2)}km`,
        totalFare: `RM${totalFare}`
      });

      if (fullRoutes[0]?.routeDetails?.length > 0) {
        const firstRoute = fullRoutes[0].routeDetails[0];
        const lastRoute = fullRoutes[0].routeDetails[fullRoutes[0].routeDetails.length - 1];
        
        setOrigin({
          lat: firstRoute.startLocation.latitude,
          lng: firstRoute.startLocation.longitude
        });
        
        setDestination({
          lat: lastRoute.endLocation.latitude,
          lng: lastRoute.endLocation.longitude
        });

        setDefaultCenter({
          lat: firstRoute.startLocation.latitude,
          lng: firstRoute.startLocation.longitude
        });
      }
    } catch (err) {
      console.error('Polyline decoding error:', err);
    }
  }, [isLoaded, fullRoutes]);

  const onLoad = (map) => {
    mapRef.current = map;
    if (combinedPath.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      combinedPath.forEach((point) => bounds.extend(point));
      map.fitBounds(bounds);
    }
  };

  const getTransitIcon = (type) => {
    switch (type) {
      case 'BUS':
        return faBus;
      case 'SUBWAY':
        return faTrain;
      default:
        return faMapMarkedAlt;
    }
  };

  const handleCloseOverlay = () => {
    // setShowMapOverlay(false);
    setSelectedStop(null);
  };

  if (loadError) return <div>Error loading map</div>;
  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div className="map-container">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultCenter}
        zoom={14}
        onLoad={onLoad}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {/* Main route polyline */}
        {combinedPath.length > 0 && (
          <Polyline
            path={combinedPath}
            options={{
              strokeColor: '#4285F4',
              strokeOpacity: 1.0,
              strokeWeight: 6,
              geodesic: true,
            }}
          />
        )}
        
        {/* Walking paths */}
        {walkingPaths.map((walk, index) => (
          <Polyline
            key={`walk-${index}`}
            path={[walk.start, walk.end]}
            options={{
              strokeColor: '#666666',
              strokeOpacity: 0.7,
              strokeWeight: 2,
              geodesic: true,
            }}
          />
        ))}
        
        {/* Origin and Destination markers */}
        {origin && (
          <Marker
            position={origin}
            label={{
              text: 'A',
              color: '#000000',
              fontWeight: 'bold',
              className: 'map-label'
            }}
          />
        )}
        
        {destination && (
          <Marker
            position={destination}
            label={{
              text: 'B',
              color: '#000000',
              fontWeight: 'bold',
              className: 'map-label'
            }}
          />
        )}

        {/* Transit stops */}
        {transitStops.map((stop, index) => (
          <Marker
            key={`stop-${index}`}
            position={stop.position}
            onClick={() => handleMarkerClick(stop)}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: stop.color,
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
            }}
          />
        ))}

        {/* Info window for selected stop */}
        {isInfoWindowOpen && selectedStop && (
          <InfoWindow
            position={selectedStop.position}
            onCloseClick={handleInfoWindowClose}
            options={{
              pixelOffset: new window.google.maps.Size(0, -30)
            }}
          >
            <div className="info-window">
              <h3 style={{ color: selectedStop.color }}>
                {selectedStop.name}
              </h3>
              <div className="info-window-row">
                <FontAwesomeIcon 
                  icon={getTransitIcon(selectedStop.vehicleType)} 
                  className="info-window-icon"
                  style={{ color: selectedStop.color }}
                />
                <span className="info-window-bold">{selectedStop.line}</span>
              </div>
              <div className="info-window-row">
                <FontAwesomeIcon icon={faClock} className="info-window-icon" />
                <span>{selectedStop.departureTime} - {selectedStop.arrivalTime}</span>
              </div>
              {selectedStop.agency && (
                <div className="info-window-section">
                  <p className="info-window-bold">Operator:</p>
                  <p>{selectedStop.agency}</p>
                  {selectedStop.phone && (
                    <p>Phone: {selectedStop.phone}</p>
                  )}
                </div>
              )}
              <p className="info-window-italic">
                {selectedStop.instruction}
              </p>
              {/* Debug log for vehicleType */}
              {console.log('vehicleType:', selectedStop.vehicleType)}
              {/* Buy Ticket Button for KTMB, ETS, KTMB Tebrau (flexible match) */}
              {(() => {
                const type = (selectedStop.vehicleType || '').toUpperCase();
                const agency = (selectedStop.agency || '').toUpperCase();
                const line = (selectedStop.line || '').toUpperCase();
                const name = (selectedStop.name || '').toUpperCase();
                const isKTMB =
                  type.includes('KTM') || type.includes('KTMB') || type.includes('ETS') || type.includes('TEBRAU') || type === 'HEAVY_RAIL' ||
                  agency.includes('KTM') || agency.includes('KTMB') ||
                  line.includes('KTM') || line.includes('KTMB') ||
                  name.includes('KTM') || name.includes('KTMB');
                return isKTMB;
              })() && (
                <a
                  href={process.env.REACT_APP_KTMB_DEEPLINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    marginTop: 12,
                    padding: '8px 18px',
                    background: '#1434A4',
                    color: '#fff',
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: '1rem',
                    textDecoration: 'none',
                    boxShadow: '0 2px 8px rgba(20,52,164,0.12)',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  Buy Ticket
                </a>
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Route summary box */}
      {routeSummary && (
        <div className="route-summary-box">
          <div className="route-summary-locations">
            <div className="route-summary-location">
              <span className="location-marker origin">A</span>
              <span className="location-text">{from}</span>
            </div>
            <div className="route-summary-location">
              <span className="location-marker destination">B</span>
              <span className="location-text">{to}</span>
            </div>
          </div>
          <div className="route-summary-detail">
            <FontAwesomeIcon icon={faClock} className="route-summary-icon" />
            <span>{routeSummary.totalDuration}</span>
          </div>
          <div className="route-summary-detail">
            <FontAwesomeIcon icon={faRoute} className="route-summary-icon" />
            <span>{routeSummary.totalDistance}</span>
          </div>
          <div className="route-summary-detail">
            <FontAwesomeIcon icon={faMoneyBill} className="route-summary-icon" />
            <span>{routeSummary.totalFare}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EpicGoogleMap;
